"use client";

import { usePrivy } from "@privy-io/react-auth";
import { useAdmin } from "logic/components";
import { useAccountName } from "logic/hooks";
import { cn } from "logic/utils";
import Link from "next/link";
import { useState } from "react";
import { Icon, IconButton } from "ui/components";

import { DevtoolsButton } from "../Devtools";
import { AccountModal } from "../Navbar/WalletButton/AccountModal";

type Props = {
  className?: string;
};

export function TopBar(props: Props) {
  const { className } = props;

  const { user } = usePrivy();
  const { isAdminMode } = useAdmin();
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  return (
    <div className={cn("flex items-center justify-end gap-2 p-4", className)}>
      {/* The frame's breadcrumb — collapse button + "Summary" — is omitted along
          with the sidebar it belongs to. */}

      {/* Carried over from `Navbar`, which this bar replaces on the loans route:
          without them the admin page has no in-app link and the devtools modal
          has no trigger. Neither is in the frame — the frame's nav lives in the
          sidebar. */}
      {isAdminMode && (
        <Link className="flex h-7.5 shrink-0 items-center justify-center bg-red-200 px-2 text-red-900" href="/admin">
          Admin
        </Link>
      )}

      <DevtoolsButton />

      <AccountButton onClick={() => setIsAccountOpen(true)} />

      {user && <AccountModal isOpen={isAccountOpen} onClose={setIsAccountOpen} user={user} />}
    </div>
  );
}

type AccountButtonProps = {
  onClick: () => void;
};

function AccountButton(props: AccountButtonProps) {
  const { onClick } = props;

  const name = useAccountName();

  // Nothing to identify while signed out, so the button is omitted entirely.
  if (!name) return null;

  return (
    <IconButton
      aria-label="Account"
      // Figma's nav button uses the 4px `icon` radius and the on-surface label
      // color, not the outlined button's own 6px radius / label color.
      className="size-auto h-9 gap-2 rounded-icon bg-surface-surface p-1.5 text-surface-on-surface"
      onClick={onClick}
    >
      <span className="text-body-sm-400 flex size-6 shrink-0 items-center justify-center rounded-icon bg-surface-inverse-surface text-surface-inverse-on-surface">
        {name.slice(0, 1).toUpperCase()}
      </span>

      <span className="text-body-sm-500 whitespace-nowrap">{name}</span>

      <Icon name="caret_down" size="small" />
    </IconButton>
  );
}
